import { useState, useCallback, useEffect } from "react";

function useLocalStorage(key, initialValue) {
  // Validate key
  if (!key || typeof key !== "string") {
    throw new Error("Storage key must be a non-empty string");
  }

  // Initialize state with a function to avoid unnecessary JSON parsing
  const [storedValue, setStoredValue] = useState(() => {
    if (typeof window === "undefined") {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      // Parse stored json or return initialValue if invalid
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Memoize setValue function
  const setValue = useCallback(
    (value) => {
      try {
        // Allow value to be a function for consistency with useState
        const valueToStore =
          value instanceof Function ? value(storedValue) : value;

        // Save state
        setStoredValue(valueToStore);

        // Save to localStorage
        if (typeof window !== "undefined") {
          if (valueToStore === undefined) {
            window.localStorage.removeItem(key);
          } else {
            window.localStorage.setItem(key, JSON.stringify(valueToStore));
          }
        }
      } catch (error) {
        console.error(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key, storedValue]
  );

  // Handle storage events from other windows/tabs
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === key && event.newValue !== event.oldValue) {
        try {
          const newValue = event.newValue
            ? JSON.parse(event.newValue)
            : initialValue;
          setStoredValue(newValue);
        } catch (error) {
          console.error(
            `Error handling storage change for key "${key}":`,
            error
          );
        }
      }
    };

    if (typeof window !== "undefined") {
      window.addEventListener("storage", handleStorageChange);
      return () => {
        window.removeEventListener("storage", handleStorageChange);
      };
    }
  }, [key, initialValue]);

  // Clear value when component unmounts with specific key
  useEffect(() => {
    return () => {
      // Optional: Clear on unmount
      // window.localStorage.removeItem(key);
    };
  }, [key]);

  return {
    storedValue,
    setValue,
    // Add remove function
    remove: useCallback(() => {
      setStoredValue(undefined);
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(key);
      }
    }, [key]),
  };
}

export default useLocalStorage;
